import React from 'react';
import {
  BRAND_NAME,
  BRAND_TAGLINE,
  brandOrganizationDescription,
} from '../constants/brand';

const AIOptimizedSchema = ({ 
  pageType = 'website',
  location = 'Bandarban',
  services = [],
  faqs = [],
  content = []
}) => {
  // Ensure services is always an array
  const safeServices = Array.isArray(services) ? services : [];
  const generateAISchema = () => {
    // AI-optimized FAQ schema for better understanding (AEO)
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What is Khuyung WedStories and what makes them unique?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": `${BRAND_TAGLINE} ${BRAND_NAME} is a professional wedding photography and cinematography team based in Chittagong Hill Tracts (CHT), Bangladesh. We specialize in capturing Indigenous group wedding traditions and ceremonies, and authentic love stories. Our unique approach combines storytelling photography with cultural preservation, documenting traditional Chakma, Marma, Tripura, and Tanchangya wedding ceremonies. With 5+ years of experience and 300+ weddings captured, we are the leading wedding photography team in CHT.`
          }
        },
        {
          "@type": "Question", 
          "name": "Where does Khuyung WedStories provide wedding photography services?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": `${BRAND_TAGLINE} ${BRAND_NAME} provides wedding photography and cinematography services across Chittagong Hill Tracts (CHT) and surrounding areas, including Bandarban, Rangamati, Khagrachhari, Cox's Bazar, and Chittagong. We travel throughout Bangladesh to capture special wedding moments and indigenous ceremonies.`
          }
        },
        {
          "@type": "Question",
          "name": "What types of wedding photography services does Khuyung WedStories offer?",
          "acceptedAnswer": {
            "@type": "Answer", 
            "text": `${BRAND_TAGLINE} ${BRAND_NAME} offers comprehensive wedding photography services including: storytelling wedding photography, wedding cinematography, pre-wedding photography, post-wedding photography, engagement photography, traditional wedding photography, indigenous wedding photography, Indigenous group wedding photography, documentary wedding photography, candid wedding photography, cinematic wedding films, and professional photo/video editing services.`
          }
        },
        {
          "@type": "Question",
          "name": "How much does Khuyung WedStories wedding photography cost?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": `${BRAND_TAGLINE} ${BRAND_NAME} wedding photography packages range from BDT 15,000 to BDT 100,000 depending on the services selected, duration, and specific requirements. We offer various packages including basic photography, premium storytelling packages, and complete cinematography services. Contact us for a personalized quote based on your wedding needs.`
          }
        },
        {
          "@type": "Question",
          "name": "What makes Khuyung WedStories the best choice for indigenous wedding photography?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": `${BRAND_TAGLINE} ${BRAND_NAME} is the best choice for indigenous wedding photography because we have deep cultural understanding of CHT traditions, specialize in Indigenous group wedding ceremonies, use storytelling techniques to preserve cultural heritage, offer authentic documentation of traditional practices, and have extensive experience with Chakma, Marma, Tripura, and Tanchangya wedding customs.`
          }
        }
      ]
    };

    // AI-optimized Organization schema (AIO / GEO entity)
    const organizationSchema = {
      "@context": "https://schema.org",
      "@type": ["Organization", "LocalBusiness", "Photographer"],
      "name": BRAND_NAME,
      "slogan": BRAND_TAGLINE,
      "alternateName": [
        "Khuyung Wedding Stories",
        "Khuyung Wedding Photography", 
        "CHT Wedding Photography",
        "Indigenous Wedding Photography CHT",
        "Indigenous Group Wedding Photography Bangladesh",
        BRAND_TAGLINE
      ],
      "description": brandOrganizationDescription(
        'Our expertise includes Chakma, Marma, Tripura, and Tanchangya wedding customs with 5+ years of experience and 300+ weddings captured across Bandarban, Rangamati, Khagrachhari, Cox\'s Bazar, and Bangladesh.'
      ),
      "url": "https://khuyungwedstories.com",
      "logo": "https://khuyungwedstories.com/albums/logo/logo.png",
      "image": "https://khuyungwedstories.com/albums/logo/logo.png",
      "telephone": "+8801601719735",
      "email": "khuyungwedstories@gmail.com",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": location,
        "addressRegion": "Chittagong Hill Tracts",
        "addressCountry": "BD"
      },
      "areaServed": [
        {
          "@type": "City",
          "name": "Bandarban"
        },
        {
          "@type": "City", 
          "name": "Rangamati"
        },
        {
          "@type": "City",
          "name": "Khagrachhari"
        },
        {
          "@type": "City",
          "name": "Cox's Bazar"
        },
        {
          "@type": "City",
          "name": "Chittagong"
        }
      ],
      "knowsAbout": [
        "Wedding Photography",
        "Wedding Cinematography",
        "Indigenous Wedding Traditions",
        "Indigenous Group Wedding Ceremonies", 
        "Chakma Wedding Photography",
        "Marma Wedding Photography",
        "Tripura Wedding Photography",
        "Tanchangya Wedding Photography",
        "CHT Wedding Photography",
        "Storytelling Photography",
        "Storytelling Wedding Photography",
        "Storytelling Wedding Photography Team",
        "Best Storytelling Wedding Photography Team",
        "Documentary Wedding Photography",
        "Candid Wedding Photography",
        "Traditional Wedding Photography",
        "Cultural Wedding Documentation",
        "Wedding Photo Editing",
        "Wedding Video Editing",
        "Best Wedding Photography Team",
        "Wedding Photography Team"
      ],
      "serviceType": [
        "Wedding Photography Services",
        "Wedding Cinematography Services",
        "Pre-wedding Photography",
        "Post-wedding Photography", 
        "Engagement Photography",
        "Traditional Wedding Photography",
        "Indigenous Wedding Photography",
        "Indigenous Group Wedding Photography",
        "Storytelling Wedding Photography",
        "Storytelling Wedding Photography Team Services",
        "Best Storytelling Wedding Photography Team Services",
        "Documentary Wedding Photography",
        "Candid Wedding Photography",
        "Cinematic Wedding Films",
        "Beach Wedding Photography",
        "Destination Wedding Photography"
      ],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Wedding Photography Services Catalog",
        "itemListElement": safeServices.map((service, index) => ({
          "@type": "Offer",
          "position": index + 1,
          "name": service.name || "Wedding Photography Service",
          "description": service.description || "Professional wedding photography and cinematography services",
          "itemOffered": {
            "@type": "Service",
            "name": service.name || "Wedding Photography Service",
            "description": service.description || "Professional wedding photography and cinematography services",
            "provider": {
              "@type": "Organization",
              "name": "Khuyung WedStories"
            }
          }
        }))
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.9",
        "reviewCount": "127",
        "bestRating": "5",
        "worstRating": "1"
      },
      "sameAs": [
        "https://www.facebook.com/khuyungwedstories",
        "https://www.instagram.com/khuyungwedstories"
      ]
    };

    return { faqSchema, organizationSchema };
  };

  const { faqSchema, organizationSchema } = generateAISchema();

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(faqSchema, null, 2)
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(organizationSchema, null, 2)
        }}
      />
    </>
  );
};

export default AIOptimizedSchema;
